import { PartnerEndpoints } from '../../../../utils/endpoints';
import { formatCount } from '../../../../utils/money';
import { useServerTable } from '../../../../hooks/useServerTable';
import { Alert, SearchInput, Skeleton, StatusPill } from '../../../ui';

const PICKER_PAGE_SIZE = 50;

export const panErrorMessages = (errors) =>
  Object.entries(errors ?? {})
    .filter(([field]) => field === 'pans' || field.startsWith('pans.'))
    .flatMap(([, messages]) => messages);

const ownershipNote = (user, subMerchantId) => {
  if (!user.sub_merchant) return null;
  if (user.sub_merchant.id === subMerchantId) return 'Already assigned';
  return `Currently assigned to ${user.sub_merchant.name}. Assigning moves it.`;
};

const noteClass = {
  own: 'text-sm text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor',
  sibling: 'text-sm text-warning-700',
};

const PanOption = ({ user, subMerchantId, checked, onToggle }) => {
  const ownedHere = Boolean(subMerchantId) && user.sub_merchant?.id === subMerchantId;
  const note = ownershipNote(user, subMerchantId);
  const inputId = `pan-option-${user.pan}`;

  return (
    <li className="flex items-start gap-3 border-b border-theme-border px-3 py-2.5 last:border-0 dark:border-themedark-border">
      <input
        id={inputId}
        type="checkbox"
        className="form-check-input mt-0.5 shrink-0"
        checked={ownedHere || checked}
        disabled={ownedHere}
        onChange={() => onToggle(user.pan)}
      />
      <label htmlFor={inputId} className="flex flex-1 flex-col gap-0.5 cursor-pointer">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-theme-headings dark:text-themedark-headings">{user.name || '—'}</span>
          <StatusPill status={user.status} />
        </span>
        <span className="text-sm tabular-nums text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor">
          {user.pan}
        </span>
        {note && <span className={ownedHere ? noteClass.own : noteClass.sibling}>{note}</span>}
      </label>
    </li>
  );
};

const PanPicker = ({ subMerchantId = null, selected, onChange }) => {
  const table = useServerTable(PartnerEndpoints.externalUsers, {
    sort: 'name',
    pageSize: PICKER_PAGE_SIZE,
    fallbackError: 'We could not load your PANs. Please try again.',
  });

  const toggle = (pan) =>
    onChange(selected.includes(pan) ? selected.filter((value) => value !== pan) : [...selected, pan]);

  const renderOptions = () => {
    if (table.loading) {
      return (
        <div className="flex flex-col gap-3 p-3">
          <Skeleton h="1rem" />
          <Skeleton h="1rem" />
          <Skeleton h="1rem" />
        </div>
      );
    }
    if (table.error) return <Alert variant="danger" className="m-3">{table.error}</Alert>;
    if (table.rows.length === 0) {
      return (
        <p className="p-3 text-sm text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor">
          No PANs match this search.
        </p>
      );
    }
    return (
      <ul>
        {table.rows.map((user) => (
          <PanOption
            key={user.pan}
            user={user}
            subMerchantId={subMerchantId}
            checked={selected.includes(user.pan)}
            onToggle={toggle}
          />
        ))}
      </ul>
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <SearchInput value={table.search} onChange={table.setSearch} placeholder="Search name, PAN, email or phone" />
      <div className="max-h-72 overflow-y-auto rounded border border-theme-border dark:border-themedark-border">
        {renderOptions()}
      </div>
      <div className="flex flex-wrap justify-between gap-2 text-sm text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor">
        <span>{formatCount(selected.length)} selected</span>
        {table.total > table.rows.length && (
          <span>
            Showing {formatCount(table.rows.length)} of {formatCount(table.total)}. Search to narrow the list.
          </span>
        )}
      </div>
    </div>
  );
};

export default PanPicker;
